/* import __COLOCATED_TEMPLATE__ from './card.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
/* eslint-disable @intercom/intercom/no-bare-strings */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { BaseConfig, BlocksDocument } from '@intercom/embercom-prosemirror-composer';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
// @ts-expect-error No types declaration for ember-copy yet.
import { copy } from 'ember-copy';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';

class NudgeComposerConfig extends BaseConfig {
  placeholder = 'Hi there we are here to help...';
  autoFocus = false;
  allowedBlocks = ['paragraph'];
  allowedInline = ['bold', 'italic'];
  tools = [{ name: 'template-inserter' }, { name: 'fallback-editor' }];

  constructor(isReadOnly = false) {
    super();
    this.isInteractivityDisabled = isReadOnly;
  }
}

interface Signature {
  Args: {
    ruleset: any; // TODO: Type this properly with the ruleset type
    isEditing: boolean;
    editRuleset: (ruleset: any) => void;
  };
  Blocks: {
    default: [];
  };
}

export default class MessengerNudgesCard extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intercomConfirmService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;

  @tracked blocksDocument = new BlocksDocument();

  objectType = objectTypes.nudge;

  get composerConfig() {
    return new NudgeComposerConfig(!this.args.isEditing);
  }

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    // Initialize blocks document with existing blocks if available
    if (this.nudge?.blocks) {
      this.blocksDocument = new BlocksDocument(this.nudge.blocks);
    }
  }

  get app() {
    return this.appService.app;
  }

  get nudge() {
    return this.args.ruleset?.rulesetLinks?.firstObject?.object;
  }

  get resolver() {
    return new AttributeInfoResolver({
      attributes: this.app.allowedAttributes,
      includeAppAttributes: false,
    });
  }

  @action onComposerChange(blocksDocument: BlocksDocument) {
    this.blocksDocument = blocksDocument;
    if (this.nudge) {
      this.nudge.blocks = blocksDocument.blocks;
    }
  }

  @action cancelEdit() {
    this.args.ruleset.rollbackAttributes();
    this.args.editRuleset(null);
  }

  @action async save() {
    await this.args.ruleset.save();
    this.args.editRuleset(null);
  }

  @action async delete() {
    let confirmOptions = {
      title: `Delete this nudge?`,
      body: `Are you sure you want to delete this nudge? This is permanent.`,
      confirmButtonText: `Delete`,
      cancelButtonText: `Cancel`,
      primaryButtonType: 'primary-destructive',
    };

    let confirmed = await this.intercomConfirmService.confirm(confirmOptions);
    if (!confirmed) {
      return;
    }

    await this.args.ruleset.destroyRecord();

    this.args.editRuleset(null);

    this.notificationsService.notifyConfirmation('Your nudge has been deleted.');
  }

  @action async activate() {
    await this.args.ruleset.save();
    await this.args.ruleset.activate();
    this.args.editRuleset(null);

    this.notificationsService.notifyConfirmation('Your nudge has been set live.');
  }

  @action async deactivate() {
    await this.args.ruleset.deactivate();

    this.notificationsService.notifyConfirmation('Your nudge has been paused.');
  }

  @action onUpdateClientPredicates(predicates: any[]) {
    this.args.ruleset.clientPredicateGroup.predicates = copy(predicates);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Messenger::Nudges::Card': typeof MessengerNudgesCard;
    'messenger/nudges/card': typeof MessengerNudgesCard;
  }
}
